import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-hot-toast";

const AudioRecorder = ({ onRecordingComplete }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [permissionError, setPermissionError] = useState(false);

    const mediaRecorderRef = useRef(null);
    const timerRef = useRef(null);
    const chunksRef = useRef([]);

    useEffect(() => {
        return () => {
            stopTimer();
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
                mediaRecorderRef.current.stop();
            }
        };
    }, []);

    const startTimer = () => {
        setRecordingTime(0);
        timerRef.current = setInterval(() => {
            setRecordingTime((prev) => prev + 1);
        }, 1000);
    };

    const stopTimer = () => {
        clearInterval(timerRef.current);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
    };

    const startRecording = async () => {
        setPermissionError(false);
        chunksRef.current = [];

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            // Determine supported mime type
            let mimeType = "audio/webm";
            if (MediaRecorder.isTypeSupported("audio/mp4")) {
                mimeType = "audio/mp4"; // Safari
            } else if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
                mimeType = "audio/webm;codecs=opus"; // Chrome/FF
            }

            const mediaRecorder = new MediaRecorder(stream, { mimeType });
            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data);
                }
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: mimeType });
                const file = new File([blob], `recording-${Date.now()}.${mimeType.split("/")[1].split(";")[0]}`, { type: mimeType });
                onRecordingComplete(file);

                // Stop all tracks
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.start(1000); // Timeslice 1s to ensure data chunks
            setIsRecording(true);
            startTimer();

        } catch (err) {
            console.error("Audio recording permission error:", err);
            setPermissionError(true);
            toast.error("Microphone access denied or not available. Please check permissions.");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            stopTimer();
        }
    };

    const cancelRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            // Remove onstop handler to prevent sending data
            mediaRecorderRef.current.onstop = null;

            // Stop tracks
            mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());

            setIsRecording(false);
            stopTimer();
            toast("Recording cancelled");
        }
    };

    if (permissionError) {
        return (
            <button
                onClick={startRecording}
                className="text-red-500 hover:text-red-700 p-2 text-xs"
                type="button"
            >
                ⚠️ Mic Error (Retry)
            </button>
        );
    }

    return (
        <div className="flex items-center gap-2">
            {!isRecording ? (
                <button
                    onClick={startRecording}
                    type="button"
                    className="p-2 transition-all duration-200 text-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-full"
                    title="Record Voice Note"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                        <line x1="12" x2="12" y1="19" y2="22" />
                    </svg>
                </button>
            ) : (
                <div className="flex items-center gap-2 bg-red-50 px-3 py-1 rounded-full border border-red-100">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-xs font-mono text-red-600 w-10">{formatTime(recordingTime)}</span>

                    <button
                        onClick={stopRecording}
                        type="button"
                        className="w-6 h-6 flex items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 shadow-sm"
                        title="Send"
                    >
                        ✓
                    </button>

                    <button
                        onClick={cancelRecording}
                        type="button"
                        className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-200 text-gray-500 hover:bg-gray-300"
                        title="Cancel"
                    >
                        ✕
                    </button>
                </div>
            )}
        </div>
    );
};

export default AudioRecorder;
